import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL + '/api' || '/api';

console.log('=== API Service Debug ===');
console.log('API_BASE_URL:', API_BASE_URL);
console.log('Environment:', process.env.NODE_ENV);
console.log('========================');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for debugging
api.interceptors.request.use((config) => {
  console.log(`API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
  
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and add response debugging
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  (error) => {
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
    const url = error.config?.url || 'unknown';
    const status = error.response?.status || 'No Response';
    
    console.error(`API Error: ${status} ${method} ${url}`);
    console.error('Error details:', error.response?.data || error.message);
    
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/admin';
    }
    return Promise.reject(error);
  }
);

// Helper function to generate time slots in specified intervals
const generateTimeSlots = (startTime, endTime, intervalMinutes) => {
  const slots = [];
  const start = new Date(`1970-01-01T${startTime}:00`);
  const end = new Date(`1970-01-01T${endTime}:00`);
  
  let current = new Date(start);
  while (current < end) {
    const timeString = current.toTimeString().slice(0, 5);
    slots.push(timeString);
    current.setMinutes(current.getMinutes() + intervalMinutes);
  }
  
  return slots;
};

export const bookingApi = {
  // Get available time slots for a specific date
  async getAvailability(date, appointmentDuration) {
    try {
      // Backend expects: /availability/{date} (route parameter, not query parameter)
      const response = await api.get(`/availability/${date}/${appointmentDuration.appointmentDuration}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching availability:', error);
      throw new Error('Failed to load available time slots');
    }
  },

  // Book an appointment
  async bookAppointment(appointmentData) {
    try {
      const response = await api.post('/appointments', appointmentData);
      return response.data;
    } catch (error) {
      console.error('Error booking appointment:', error);
      const message = error.response?.data?.message || 'Failed to book appointment';
      throw new Error(message);
    }
  },

  // Get appointment details
  async getAppointment(appointmentId) {
    try {
      const response = await api.get(`/appointments/${appointmentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching appointment:', error);
      throw new Error('Failed to load appointment details');
    }
  },

  // Update appointment (Note: Backend doesn't have PUT endpoint, only cancel)
  async updateAppointment(appointmentId, updateData) {
    try {
      const response = await api.put(`/appointments/${appointmentId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating appointment:', error);
      throw new Error('Failed to update appointment');
    }
  },

  // Cancel appointment
  async cancelAppointment(appointmentId, reason = '') {
    try {
      // Backend expects: POST /appointments/{id}/cancel (not DELETE)
      const response = await api.post(`/appointments/${appointmentId}/cancel`, {
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Error canceling appointment:', error);
      throw new Error('Failed to cancel appointment');
    }
  },

  // Get available dates for calendar
  async getAvailableDates(startDate = null, endDate = null) {
    try {
      let url = '/available-dates';
      const params = new URLSearchParams();
      
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching available dates:', error);
      throw new Error('Failed to load available dates');
    }
  },

  // Confirm appointment via XHR (for email confirmation)
  async confirmAppointment(appointmentId) {
    try {
      const response = await api.post(`/appointments/${appointmentId}/confirm-xhr`);
      return response.data;
    } catch (error) {
      console.error('Error confirming appointment:', error);
      if (error.response?.status === 409) {
        throw new Error('This appointment slot is no longer available. Please book a new appointment.');
      } else if (error.response?.status === 404) {
        throw new Error('Appointment not found.');
      } else if (error.response?.status === 400) {
        throw new Error('Appointment cannot be confirmed.');
      }
      throw new Error('Failed to confirm appointment');
    }
  },
};

export const adminApi = {
  // Google OAuth login
  async googleLogin(credential) {
    try {
      // Backend expects 'token' field, not 'credential'
      const response = await api.post('/auth/google', { token: credential });
      const { token, email } = response.data;
      localStorage.setItem('authToken', token);
      return { token, user: { email } };
    } catch (error) {
      console.error('Error during Google login:', error);
      throw new Error('Authentication failed');
    }
  },

  // Logout
  logout() {
    localStorage.removeItem('authToken');
  },

  // Get all appointments (admin only)
  async getAppointments(startDate, endDate) {
    try {
      // Backend doesn't support date filtering via query params yet
      // TODO: Add date filtering support to backend if needed
      const response = await api.get('/manage/appointments');
      return response.data;
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw new Error('Failed to load appointments');
    }
  },

  // Create availability slots (admin only) - Legacy method
  async createAvailability(availabilityData) {
    try {
      const response = await api.post('/manage/availability', availabilityData);
      return response.data;
    } catch (error) {
      console.error('Error creating availability:', error);
      throw new Error('Failed to create availability');
    }
  },

  // Create advanced availability slots with time ranges and recurring options
  async createAdvancedAvailability(availabilityData) {
    try {
      const response = await api.post('/manage/availability', availabilityData);
      return response.data;
    } catch (error) {
      console.error('Error creating advanced availability:', error);
      throw new Error('Failed to create availability');
    }
  },

  // Get availability slots for admin management
  async getAvailabilitySlots(startDate, endDate) {
    try {
      const response = await api.get(`/manage/availability-slots/${startDate}/${endDate}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching availability slots:', error);
      throw new Error('Failed to load availability slots');
    }
  },

  // Update availability slot
  async updateAvailabilitySlot(slotId, updateData) {
    try {
      const response = await api.put(`/manage/availability-slot/${slotId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating availability slot:', error);
      throw new Error('Failed to update availability slot');
    }
  },

  // Delete availability slot
  async deleteAvailabilitySlot(slotId, deleteRecurringSeries = false) {
    try {
      const response = await api.delete(`/manage/availability-slot/${slotId}?deleteRecurringSeries=${deleteRecurringSeries}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting availability slot:', error);
      throw new Error('Failed to delete availability slot');
    }
  },

  // Create appointment as admin
  async createAdminAppointment(appointmentData) {
    try {
      const response = await api.post('/manage/create-appointment', appointmentData);
      return response.data;
    } catch (error) {
      console.error('Error creating admin appointment:', error);
      if (error.response?.status === 409) {
        throw new Error('Another appointment already exists at this time');
      }
      throw new Error('Failed to create appointment');
    }
  },

  // Cancel appointment as admin (Backend doesn't have separate admin cancel endpoint)
  async cancelAppointmentAsAdmin(appointmentId, reason, notifyClient = true) {
    try {
      // Use the same cancel endpoint as regular users
      const response = await api.post(`/appointments/${appointmentId}/cancel`, {
        reason,
        notifyClient
      });
      return response.data;
    } catch (error) {
      console.error('Error canceling appointment:', error);
      throw new Error('Failed to cancel appointment');
    }
  },

  // Get appointments for a specific date (for admin timeline view)
  async getAppointmentsForDate(date) {
    try {
      const response = await api.get('/manage/appointments');
      // Filter appointments for the specific date on the frontend
      const appointments = response.data || [];
      return appointments.filter(appointment => appointment.date === date);
    } catch (error) {
      console.error('Error fetching appointments for date:', error);
      throw new Error('Failed to load appointments for date');
    }
  },

  // Get weekly availability with 15-minute time slots for admin scheduling
  async getWeeklyAvailability(startDate, endDate) {
    try {
      const response = await api.get(`/manage/availability-slots/${startDate}/${endDate}`);
      const availabilitySlots = response.data || [];
      
      // Transform availability slots into 15-minute time slots
      const weeklyAvailability = {};
      
      availabilitySlots.forEach(slot => {
        if (!weeklyAvailability[slot.date]) {
          weeklyAvailability[slot.date] = [];
        }
        
        // Generate 15-minute time slots within the availability window
        if (slot.time && slot.endTime) {
          const startTime = slot.time;
          const endTime = slot.endTime;
          const timeSlots = generateTimeSlots(startTime, endTime, 15);
          weeklyAvailability[slot.date].push(...timeSlots);
        }
      });
      
      return weeklyAvailability;
    } catch (error) {
      console.error('Error fetching weekly availability:', error);
      throw new Error('Failed to load weekly availability');
    }
  },
};

export const promotionsApi = {
  async submitLead(slug, form) {
    try {
      // Execute reCAPTCHA v3
      const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6Lc-l2YrAAAAAO11Go3gHmqxkMZtXrlTxMVg6VRY';
      let token = '';
      if (window.grecaptcha) {
        await new Promise((resolve) => {
          window.grecaptcha.ready(resolve);
        });
        token = await window.grecaptcha.execute(siteKey, { action: 'promo_lead' });
      }
      const payload = { ...form, recaptchaToken: token, language: form.language };
      const response = await api.post(`/promotions/${slug}/lead`, payload);
      return response.data;
    } catch (error) {
      console.error('Error submitting promo lead:', error);
      const message = error.response?.data?.message || 'Failed to submit request';
      throw new Error(message);
    }
  }
};

export default api; 